/**
 * Modifier les données d'un utilisateur, depuis l'administration.
 *
 * ## Pourquoi ce formulaire a dû être écrit
 *
 * La demande du 20 septembre 2026 disait : « le module existe déjà dans la
 * partie Gestion CDV - Parents, pas la peine de la refaire ». Vérifié : il
 * n'existe pas. Dans `ParentsManagement.tsx`, le bouton crayon de la liste et
 * le bouton « Modifier » du pied de la fiche **n'ont aucun `onClick`**, et la
 * fiche qu'ils ouvrent est en lecture seule. Il n'y avait rien à réutiliser.
 *
 * ## Et pourquoi il ne réutilise pas `ProfilePage`
 *
 * `ProfilePage` a bien les quatorze champs, mais il est soudé au compte
 * CONNECTÉ : il écrit par `updateProfile()`, qui vise `user.id` du store
 * d'authentification. Monté dans l'administration, il aurait modifié la fiche
 * de l'administrateur avec les données du parent, sans la moindre erreur
 * visible. C'est le pire des défauts possibles ici.
 *
 * ## Ce qu'il n'écrit pas
 *
 * Le **rôle** (RPC `admin_change_user_role`) et l'**abonnement** (fonctions
 * edge). Les deux sont affichés en lecture sur la fiche, avec leur chemin.
 *
 * ## Et il n'envoie que ce qui a changé
 *
 * `champsModifies` fait le tri. Envoyer la fiche entière écraserait une
 * colonne qu'un autre écran — l'espace parent, un import — vient de modifier.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Check, Loader2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  lireFicheUtilisateur, majFicheUtilisateur, type FicheComplete,
} from '../../lib/utilisateurs/fiche.supabase';
import {
  champsModifies, nomAffiche, validerFiche, type Fiche,
} from '../../lib/utilisateurs/fiche';
import { messageErreur } from '../../lib/payments/erreur';
import { ficheUtilisateur } from '../../lib/navigation/chemins';

export default function AdminUserEditionPage() {
  const { userId = '' } = useParams();
  const naviguer = useNavigate();

  const [origine, setOrigine] = useState<FicheComplete | null>(null);
  const [saisie, setSaisie] = useState<Fiche | null>(null);
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const f = await lireFicheUtilisateur(userId);
      if (!f) { setErreur('Aucun compte ne porte cet identifiant.'); return; }
      setOrigine(f);
      setSaisie(f.fiche);
    } catch (e) {
      setErreur(messageErreur(e, 'Fiche illisible'));
    } finally {
      setChargement(false);
    }
  }, [userId]);

  useEffect(() => { void charger(); }, [charger]);

  if (chargement) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Lecture de la fiche…
      </p>
    );
  }

  if (erreur && !saisie) {
    return (
      <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {erreur}
      </p>
    );
  }

  if (!saisie || !origine) return null;

  const reproches = validerFiche(saisie);
  const modifies = champsModifies(origine.fiche, saisie);
  const nbModifies = Object.keys(modifies).length;

  const set = (champ: keyof Fiche) => (v: string | boolean) =>
    setSaisie({ ...saisie, [champ]: v } as Fiche);

  const enregistrer = async () => {
    if (reproches.length > 0 || nbModifies === 0) return;
    setEnvoi(true);
    try {
      await majFicheUtilisateur(userId, modifies);
      toast.success(`${nbModifies} champ${nbModifies > 1 ? 's' : ''} enregistré${nbModifies > 1 ? 's' : ''}`);
      naviguer(ficheUtilisateur(userId));
    } catch (e) {
      toast.error(messageErreur(e, "L'enregistrement a été refusé"));
    } finally {
      setEnvoi(false);
    }
  };

  const reproche = (champ: keyof Fiche) => reproches.find((r) => r.champ === champ)?.message;

  return (
    <div className="space-y-6">
      <Link
        to={ficheUtilisateur(userId)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" /> Fiche du compte
      </Link>

      <div>
        <h1 className="text-2xl font-bold">Modifier — {nomAffiche(origine.fiche)}</h1>
        <p className="text-gray-600">
          Le rôle et l'abonnement à la lettre d'information ne se changent pas ici :
          ils passent par leurs propres chemins.
        </p>
      </div>

      <div className="space-y-5 rounded-xl border border-gray-200 bg-white p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Texte libelle="Courriel" valeur={saisie.email} onChange={set('email')}
                 reproche={reproche('email')} />
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Type de compte</label>
            <select
              value={saisie.accountType}
              onChange={(e) => set('accountType')(e.target.value)}
              className="form-input w-full"
            >
              <option value="parent">Parent</option>
              <option value="organisation">Organisation</option>
              <option value="other">Autre</option>
            </select>
          </div>
          <Texte libelle="Prénom" valeur={saisie.prenom ?? ''} onChange={set('prenom')} />
          <Texte libelle="Nom" valeur={saisie.nom ?? ''} onChange={set('nom')} />
          <Texte libelle="Téléphone" valeur={saisie.telephone ?? ''} onChange={set('telephone')} />
          <Texte libelle="Second téléphone" valeur={saisie.telephone2 ?? ''} onChange={set('telephone2')} />
          <Texte libelle="Adresse" valeur={saisie.adresse ?? ''} onChange={set('adresse')} />
          <div className="grid grid-cols-3 gap-3">
            <Texte libelle="Code postal" valeur={saisie.cpostal ?? ''} onChange={set('cpostal')}
                   reproche={reproche('cpostal')} />
            <div className="col-span-2">
              <Texte libelle="Localité" valeur={saisie.localite ?? ''} onChange={set('localite')} />
            </div>
          </div>
          <Texte libelle="Numéro national" valeur={saisie.nnational ?? ''} onChange={set('nnational')} />
          {saisie.accountType === 'organisation' && (
            <>
              <Texte libelle="Nom de l'organisation" valeur={saisie.organisationName ?? ''}
                     onChange={set('organisationName')} reproche={reproche('organisationName')} />
              <Texte libelle="Numéro d'entreprise" valeur={saisie.companyNumber ?? ''}
                     onChange={set('companyNumber')} />
            </>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={saisie.isLegalGuardian}
            onChange={(e) => set('isLegalGuardian')(e.target.checked)}
            className="h-4 w-4"
          />
          Tuteur légal
        </label>
      </div>

      {/*
        Ce qui va être écrit, nommé, avant de l'écrire. Un formulaire qui
        enregistre sans dire ce qu'il touche laisse croire qu'il touche tout.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-700">
          {nbModifies === 0
            ? 'Rien n’a changé : aucune écriture ne partira.'
            : `${nbModifies} champ${nbModifies > 1 ? 's' : ''} à enregistrer — ${Object.keys(modifies).join(', ')}`}
        </p>
        <button
          onClick={() => void enregistrer()}
          disabled={envoi || nbModifies === 0 || reproches.length > 0}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {envoi ? <Loader2 className="h-4 w-4 animate-spin" />
                 : nbModifies === 0 ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          Enregistrer
        </button>
      </div>

      {reproches.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {reproches.map((r) => <li key={r.champ}>{r.message}</li>)}
        </ul>
      )}
    </div>
  );
}

function Texte({
  libelle, valeur, onChange, reproche,
}: {
  libelle: string;
  valeur: string;
  onChange: (v: string) => void;
  reproche?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">{libelle}</label>
      <input
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        className={`form-input w-full ${reproche ? 'border-red-400' : ''}`}
      />
      {reproche && <p className="mt-1 text-xs text-red-700">{reproche}</p>}
    </div>
  );
}
